// Result files from a tumour test done elsewhere (libra somatic-vendor-files):
// upload, wait for the server's check, then either start a somatic analysis from
// the file (a result bundle's small variants, registered as an upload) or add it
// to a somatic analysis (somatic-imports, through useSomaticImport).
//
// Nothing here names a format, a vendor or a test: every text the server serves is
// a neutral sentence and is shown as sent (refusalSentence covers anything
// unexpected). Start is offered when the record says an analysis can start from
// the file (can_start_analysis), else its start_refusal text is shown; Add is
// offered when the file has an importer.
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useSomaticImport } from 'src/api/somaticStructural/imports'
import {
  STATUS_UPLOADED,
  canAddToAnalysis,
  canStartAnalysis,
  UPLOAD_FAILED_TEXT,
  fetchSmallVariants,
  fetchVendorFile,
  registrationOutcome,
  requestSmallVariants,
  startRefusalText,
  uploadVendorFile,
  vendorFileRefusal,
} from 'src/api/somaticStructural/vendorFiles'
import MuiV5Bridge from 'src/theme/MuiV5Bridge'
import { fData } from 'src/utils/formatNumber'
import { refusalSentence } from 'src/utils/importRefusal'
import AnalysisSearchSelect from 'src/views/AnalysisView/AnnotationView/components/AnalysisSearchSelect'
import ImportOutcome from 'src/views/SomaticStructuralView/ImportOutcome'

export const POLL_MS = 3000

// The analyses a result file can be added to: somatic DNA and tumour RNA, and
// those whose kind cannot be told (the server checks every pick again).
const ADD_TO_KINDS = ['somatic_dna', 'rna', 'unknown']

// Start an analysis from a checked file (a result bundle, or a single file that is
// itself a small-variant file): the server registers its small variants as an
// upload and verifies it, then the New analysis panel opens on the verified rows.
// Offered only when the record says so (can_start_analysis).
const StartFromFile = ({ record, onStartAnalysis }) => {
  const samples = Array.isArray(record.samples) ? record.samples : []
  const [sample, setSample] = useState(samples.length === 1 ? samples[0].key : '')
  const [requested, setRequested] = useState(false)
  // Polled while the registration is pending or its file is being verified
  // (react-query 3.16 takes no refetchInterval function).
  const [polling, setPolling] = useState(false)
  const [refused, setRefused] = useState(null)
  const startedRef = useRef(false)
  const needsSample = samples.length > 1 && !sample
  const queryClient = useQueryClient()
  const progressKey = ['vendor-small-variants', record.id, sample]

  const progress = useQuery(progressKey, () => fetchSmallVariants(record.id, sample), {
    enabled: requested,
    refetchInterval: polling ? POLL_MS : false,
    onSuccess: (data) => {
      if (registrationOutcome(data?.registration).state !== 'waiting') setPolling(false)
    },
  })
  const outcome = requested && progress.data ? registrationOutcome(progress.data.registration) : null
  const busy = requested && !refused && (!outcome || outcome.state === 'waiting')

  useEffect(() => {
    if (!outcome || startedRef.current) return
    if (outcome.state === 'ready') {
      startedRef.current = true
      onStartAnalysis(outcome.readyIds)
    } else if (outcome.state === 'refused') {
      setRefused(outcome.text)
      setRequested(false)
    }
  }, [outcome, onStartAnalysis])

  const start = async () => {
    setRefused(null)
    startedRef.current = false
    // A second try never reads the answer of the first.
    queryClient.removeQueries(progressKey, { exact: true })
    try {
      const result = await requestSmallVariants(record.id, sample || undefined)
      if (result.state === 'refused') {
        setRefused(result.text)
        return
      }
      setRequested(true)
      setPolling(true)
    } catch (error) {
      setRefused(refusalSentence(null))
    }
  }

  return (
    <Stack spacing={1}>
      {samples.length > 1 && (
        <TextField
          select
          size="small"
          label="Sample"
          value={sample}
          onChange={(e) => setSample(e.target.value)}
          disabled={busy}
          inputProps={{ 'data-testid': 'result-sample' }}
        >
          {samples.map((s) => (
            <MenuItem key={s.key} value={s.key}>
              {s.label || s.key}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Box>
        <Button
          variant="contained"
          size="small"
          onClick={start}
          disabled={busy || needsSample}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          data-testid="result-start"
        >
          Start an analysis from it
        </Button>
      </Box>
      {busy && (
        <Typography variant="caption" color="text.secondary" data-testid="result-start-waiting">
          {outcome?.checking
            ? 'Checking the variant file. The New analysis panel opens when it is ready.'
            : 'Preparing the analysis. The New analysis panel opens when it is ready.'}
        </Typography>
      )}
      {refused && (
        <Alert severity="warning" data-testid="result-start-refused">
          {refused}
        </Alert>
      )}
    </Stack>
  )
}

StartFromFile.propTypes = {
  record: PropTypes.object.isRequired,
  onStartAnalysis: PropTypes.func.isRequired,
}

// Add a checked file to one somatic analysis: the given one, or one the user picks.
const AddToAnalysis = ({ record, analysisId }) => {
  const [picked, setPicked] = useState(null)
  const awId = analysisId ?? picked?.analysis_id ?? null
  const { outcome, busy, start, confirm, cancel } = useSomaticImport(awId)
  const added = outcome?.state === 'imported'

  return (
    <Stack spacing={1}>
      {analysisId == null && (
        <AnalysisSearchSelect
          value={picked}
          onChange={(next) => {
            setPicked(next)
            cancel()
          }}
          forTumour={false}
          kinds={ADD_TO_KINDS}
          label="Analysis"
          disabled={busy}
        />
      )}
      <Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => start(record.id)}
          disabled={busy || awId == null || added}
          data-testid="result-add"
        >
          Add to this analysis
        </Button>
      </Box>
      {added && (
        <Alert severity="success" data-testid="result-added">
          Added to the analysis.
        </Alert>
      )}
      <ImportOutcome outcome={outcome} busy={busy} onConfirm={confirm} onCancel={cancel} />
    </Stack>
  )
}

AddToAnalysis.propTypes = {
  record: PropTypes.object.isRequired,
  analysisId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

AddToAnalysis.defaultProps = { analysisId: null }

// One file: its upload, the server's check, then what can be done with it.
export const ResultFileRow = ({ file, started, analysisId, onStartAnalysis, canAnalyse }) => {
  const [progress, setProgress] = useState(0)
  const [uploaded, setUploaded] = useState(null)
  // The sentence of a failed upload, or null.
  const [failed, setFailed] = useState(null)
  const [attempt, setAttempt] = useState(0)
  // Polled while the server is still checking the file.
  const [polling, setPolling] = useState(false)

  useEffect(() => {
    if (!started) return undefined
    let live = true
    setFailed(null)
    setProgress(0)
    uploadVendorFile(file, { onProgress: (p) => live && setProgress(p) }).then(
      (record) => {
        if (!live) return
        setUploaded(record)
        setPolling(record?.status === STATUS_UPLOADED)
      },
      (error) => live && setFailed(error?.text || UPLOAD_FAILED_TEXT),
    )
    return () => {
      live = false
    }
  }, [file, started, attempt])

  const checked = useQuery(['vendor-file', uploaded?.id], () => fetchVendorFile(uploaded.id), {
    enabled: Boolean(uploaded?.id) && polling,
    refetchInterval: polling ? POLL_MS : false,
    onSuccess: (data) => {
      if (data?.status !== STATUS_UPLOADED) setPolling(false)
    },
  })
  const record = checked.data || uploaded

  const startable = canStartAnalysis(record)
  const addable = canAddToAnalysis(record)
  const checking = record?.status === STATUS_UPLOADED

  let body = null
  if (failed) {
    body = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => setAttempt((n) => n + 1)}>
            Retry
          </Button>
        }
        data-testid="result-upload-failed"
      >
        {failed}
      </Alert>
    )
  } else if (started && !record) {
    body = <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
  } else if (checking) {
    body = (
      <Stack direction="row" spacing={1} alignItems="center" data-testid="result-checking">
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          Checking the file
        </Typography>
      </Stack>
    )
  } else if (record && !startable && !addable) {
    body = (
      <Alert severity="warning" data-testid="result-refused">
        {vendorFileRefusal(record)}
      </Alert>
    )
  } else if (record && !canAnalyse) {
    // An upload-only account uploads and checks the file; starting or extending
    // an analysis from it is left to colleagues with analysis permission.
    body = (
      <Typography variant="caption" color="text.secondary" data-testid="result-checked">
        Uploaded and checked.
      </Typography>
    )
  } else if (record) {
    body = (
      <Stack spacing={2} data-testid="result-actions">
        {startable ? (
          <StartFromFile record={record} onStartAnalysis={onStartAnalysis} />
        ) : (
          startRefusalText(record) && (
            <Typography variant="caption" color="text.secondary" data-testid="result-start-refusal">
              {startRefusalText(record)}
            </Typography>
          )
        )}
        {addable && <AddToAnalysis record={record} analysisId={analysisId} />}
      </Stack>
    )
  }

  return (
    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }} data-testid="result-file">
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: body ? 1 : 0, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap title={file.name}>
          {file.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {fData(file.size)}
        </Typography>
      </Stack>
      {body}
    </Box>
  )
}

ResultFileRow.propTypes = {
  file: PropTypes.object.isRequired,
  started: PropTypes.bool,
  analysisId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onStartAnalysis: PropTypes.func.isRequired,
  // False for an upload-only account: the row stops at the check.
  canAnalyse: PropTypes.bool,
}

ResultFileRow.defaultProps = { started: false, analysisId: null, canAnalyse: true }

const ResultFileDialog = ({ open, files: initialFiles, analysisId, onClose, onStartAnalysis, canAnalyse }) => {
  const [files, setFiles] = useState([])
  const [started, setStarted] = useState(false)
  const inputRef = useRef(null)

  // A fresh list each time the dialog opens, with the files it was opened with.
  useEffect(() => {
    if (!open) return
    setFiles(initialFiles || [])
    setStarted(false)
  }, [open, initialFiles])

  const addFiles = useCallback((list) => {
    const picked = Array.from(list || [])
    if (picked.length) setFiles((prev) => [...prev, ...picked])
  }, [])

  const startAnalysis = useCallback(
    (vcfFileIds) => {
      onStartAnalysis(vcfFileIds)
      onClose()
    },
    [onStartAnalysis, onClose],
  )

  return (
    <MuiV5Bridge>
      <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Tumour test results</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Results from a tumour test done by another laboratory. Each file is checked once uploaded; then start an
            analysis from it or add it to an analysis.
          </Typography>
          <Stack spacing={1}>
            {files.map((file, index) => (
              <ResultFileRow
                // eslint-disable-next-line react/no-array-index-key
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                started={started}
                analysisId={analysisId}
                onStartAnalysis={startAnalysis}
                canAnalyse={canAnalyse}
              />
            ))}
          </Stack>
          {!started && (
            <Box sx={{ mt: files.length ? 1.5 : 0 }}>
              <Button variant="outlined" size="small" onClick={() => inputRef.current?.click()}>
                {files.length ? 'Add more files' : 'Choose files'}
              </Button>
              <input
                ref={inputRef}
                type="file"
                multiple
                hidden
                data-testid="result-file-input"
                onChange={(e) => {
                  addFiles(e.target.files)
                  e.target.value = ''
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{started ? 'Close' : 'Cancel'}</Button>
          {!started && (
            <Button
              variant="contained"
              onClick={() => setStarted(true)}
              disabled={!files.length}
              data-testid="result-upload"
            >
              Upload
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </MuiV5Bridge>
  )
}

ResultFileDialog.propTypes = {
  open: PropTypes.bool,
  files: PropTypes.array,
  // The analysis "Add to this analysis" adds to; without it the user picks one.
  analysisId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  // Called with the registered upload ids once an analysis can start from a file.
  onStartAnalysis: PropTypes.func.isRequired,
  // False for an upload-only account: files are uploaded and checked only.
  canAnalyse: PropTypes.bool,
}

ResultFileDialog.defaultProps = { open: false, files: [], analysisId: null, canAnalyse: true }

export default ResultFileDialog
